import type { FC, ReactNode } from "react";
import dayjs from "dayjs";
import { DatePicker } from "antd";
import { Select } from "../../../shared";
import {
  useFutureBonusLeagues,
  useFutureBonusSports,
  useFutureBonusTraits,
  type FutureBonusTrait,
} from "../../../../queries/dynamicBonuses/useFutureBonusLookups";
import { AppStrings } from "../../../../utils/constants/AppStrings";
import { rememberRestrictionDisplayName } from "./restrictionDisplayNames";

type CommonSelectProps = {
  value?: string;
  onChange?: (value: string | undefined) => void;
  disabled?: boolean;
};

const failedLoadContent = (
  testId: string,
  message: string,
  refetch: () => void,
): ReactNode => (
  <span data-testid={testId}>
    {message}{" "}
    <button type="button" onClick={() => void refetch()}>
      {AppStrings.RETRY}
    </button>
  </span>
);

export const FutureBonusSportSelect: FC<CommonSelectProps> = ({
  value,
  onChange,
  disabled,
}) => {
  const { data, isLoading, isError, refetch } = useFutureBonusSports(!disabled);
  return (
    <Select
      showSearch
      optionFilterProp="label"
      placeholder={AppStrings.PAM_DYNAMIC_BONUS_FUTURE_BONUS_SPORT_PLACEHOLDER}
      disabled={disabled}
      loading={isLoading}
      status={isError ? "error" : undefined}
      notFoundContent={
        isError
          ? failedLoadContent(
              "future-bonus-sport-error",
              AppStrings.PAM_DYNAMIC_BONUS_FUTURE_BONUS_SPORTS_LOAD_FAILED,
              refetch,
            )
          : undefined
      }
      value={value || undefined}
      onChange={(next) => onChange?.(next ? String(next) : undefined)}
      options={(data ?? []).map((sport) => ({
        value: sport.id,
        label: sport.name,
      }))}
      allowClear
    />
  );
};

export const FutureBonusLeagueSelect: FC<
  CommonSelectProps & { sportId?: string }
> = ({ value, onChange, disabled, sportId }) => {
  const { data, isLoading, isError, refetch } = useFutureBonusLeagues(sportId);
  return (
    <Select
      showSearch
      optionFilterProp="label"
      placeholder={
        sportId
          ? AppStrings.PAM_DYNAMIC_BONUS_FUTURE_BONUS_LEAGUE_PLACEHOLDER
          : AppStrings.PAM_DYNAMIC_BONUS_FUTURE_BONUS_LEAGUE_NEEDS_SPORT
      }
      disabled={disabled || !sportId}
      loading={isLoading}
      status={isError ? "error" : undefined}
      notFoundContent={
        isError
          ? failedLoadContent(
              "future-bonus-league-error",
              AppStrings.PAM_DYNAMIC_BONUS_FUTURE_BONUS_LEAGUES_LOAD_FAILED,
              refetch,
            )
          : undefined
      }
      value={value || undefined}
      onChange={(next) => {
        const selected = next ? String(next) : undefined;
        if (selected) {
          const league = (data ?? []).find((item) => item.id === selected);
          if (league?.name) {
            rememberRestrictionDisplayName(
              selected,
              league.country_name
                ? `${league.name} (${league.country_name})`
                : league.name,
            );
          }
        }
        onChange?.(selected);
      }}
      options={(data ?? []).map((league) => ({
        value: league.id,
        label: league.country_name
          ? `${league.name} (${league.country_name})`
          : league.name,
      }))}
      allowClear
    />
  );
};

export type FutureBonusTraitChange = {
  trait_id?: string;
  trait_name?: string;
  market_settlement_date?: string;
  eligible_market_types?: string[];
};

export const FutureBonusTraitSelect: FC<
  CommonSelectProps & {
    sportId?: string;
    leagueId?: string;
    onTraitResolved?: (next: FutureBonusTraitChange) => void;
  }
> = ({ value, onChange, disabled, sportId, leagueId, onTraitResolved }) => {
  const { data, isLoading, isError, refetch } = useFutureBonusTraits(
    sportId,
    leagueId,
  );

  const applyTrait = (trait: FutureBonusTrait | undefined) => {
    onChange?.(trait?.trait_id);
    onTraitResolved?.({
      trait_id: trait?.trait_id,
      trait_name: trait?.display_name,
      market_settlement_date: trait?.market_settlement_date ?? undefined,
      eligible_market_types: trait?.eligible_market_types,
    });
  };

  return (
    <Select
      showSearch
      optionFilterProp="label"
      placeholder={
        leagueId
          ? AppStrings.PAM_DYNAMIC_BONUS_FUTURE_BONUS_TRAIT_PLACEHOLDER
          : AppStrings.PAM_DYNAMIC_BONUS_FUTURE_BONUS_TRAIT_NEEDS_LEAGUE
      }
      disabled={disabled || !leagueId}
      loading={isLoading}
      status={isError ? "error" : undefined}
      notFoundContent={
        isError
          ? failedLoadContent(
              "future-bonus-trait-error",
              AppStrings.PAM_DYNAMIC_BONUS_FUTURE_BONUS_TRAITS_LOAD_FAILED,
              refetch,
            )
          : undefined
      }
      value={value || undefined}
      onChange={(next) => {
        const selected = (data ?? []).find((trait) => trait.trait_id === next);
        applyTrait(selected);
      }}
      options={(data ?? []).map((trait) => ({
        value: trait.trait_id,
        label: trait.market_settlement_date
          ? trait.display_name
          : `${trait.display_name}${AppStrings.PAM_DYNAMIC_BONUS_FUTURE_BONUS_TRAIT_NO_DATE_SUFFIX}`,
        disabled: !trait.market_settlement_date,
      }))}
      allowClear
    />
  );
};

export const FutureBonusSettlementDisplay: FC<{ value?: string }> = ({
  value,
}) => {
  const parsed = value ? dayjs(value) : null;
  const display = parsed?.isValid() ? parsed.format("YYYY-MM-DD HH:mm") : "";
  return (
    <input
      className="ant-input"
      readOnly
      disabled
      value={display}
      placeholder={
        display ? undefined : AppStrings.PAM_DYNAMIC_BONUS_FUTURE_BONUS_NO_SETTLEMENT
      }
      aria-label={AppStrings.PAM_DYNAMIC_BONUS_FUTURE_BONUS_SETTLEMENT_LABEL}
    />
  );
};

type ExpiryPickerProps = {
  id?: string;
  value?: string;
  onChange?: (value: string | undefined) => void;
  disabled?: boolean;
};

/** Stores ISO-8601 instants, same as DateRangeField: local wall clock → UTC. */
export const FutureBonusExpiryPicker: FC<ExpiryPickerProps> = ({
  id,
  value,
  onChange,
  disabled,
}) => {
  const parsed = value ? dayjs(value) : null;
  return (
    <DatePicker
      id={id}
      showTime={{ format: "HH:mm" }}
      format="YYYY-MM-DD HH:mm"
      value={parsed?.isValid() ? parsed : null}
      onChange={(next) => onChange?.(next ? next.toISOString() : undefined)}
      disabled={disabled}
      style={{ width: "100%" }}
      placeholder={AppStrings.PAM_DYNAMIC_BONUS_FUTURE_BONUS_EXPIRY_PLACEHOLDER}
      aria-label={AppStrings.PAM_DYNAMIC_BONUS_FUTURE_BONUS_EXPIRY_LABEL}
    />
  );
};
